import { useEffect, useState } from 'react';
import { useAccountApi } from '../store';
import ApplicationBar from '../components/shared/ApplicationBar';
import Footer from '../components/footers/Footer';
import Loader from '../components/shared/Loader';

interface PlatformStats {
    users: { total: number; active: number; suspended: number; new_30d: number };
    events: { total: number; upcoming: number; completed: number };
    tickets: { total: number; new_30d: number; cancelled: number };
    revenue: {
        all_time_commission: number;
        all_time_gross: number;
        commission_30d: number;
        gross_30d: number;
    };
    support: { open_threads: number };
}

const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);

const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

interface StatCardProps {
    label: string;
    value: string | number;
    sub?: string;
    color?: string;
}

const StatCard = ({ label, value, sub, color = 'bg-white' }: StatCardProps) => (
    <div className={`${color} rounded-xl shadow-sm border border-gray-100 p-5`}>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
    </div>
);

const Dashboard = () => {
    const { getPlatformStats } = useAccountApi();
    const [stats, setStats] = useState<PlatformStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = 'FestiShare Support - Dashboard';
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const result = await getPlatformStats();
                if (result?.res === 'success') {
                    setStats(result.data);
                } else {
                    setError('Failed to load platform stats.');
                }
            } catch {
                setError('An unexpected error occurred.');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mt-6 mb-24 mx-auto max-w-3xl px-4">
                <h1 className="text-xl font-bold text-gray-900 mb-6">Platform Dashboard</h1>

                {loading && <Loader />}

                {error && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {stats && (
                    <div className="space-y-6">
                        {/* Users */}
                        <section>
                            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                Users
                            </h2>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <StatCard label="Total Users" value={fmt(stats.users.total)} />
                                <StatCard
                                    label="New (30d)"
                                    value={fmt(stats.users.new_30d)}
                                    color="bg-violet-50"
                                />
                                <StatCard
                                    label="Active"
                                    value={fmt(stats.users.active)}
                                    color="bg-green-50"
                                />
                                <StatCard
                                    label="Suspended"
                                    value={fmt(stats.users.suspended)}
                                    color={stats.users.suspended > 0 ? 'bg-red-50' : 'bg-white'}
                                />
                            </div>
                        </section>

                        {/* Events */}
                        <section>
                            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                Events
                            </h2>
                            <div className="grid grid-cols-3 gap-3">
                                <StatCard label="Total Events" value={fmt(stats.events.total)} />
                                <StatCard
                                    label="Upcoming"
                                    value={fmt(stats.events.upcoming)}
                                    color="bg-blue-50"
                                />
                                <StatCard
                                    label="Completed"
                                    value={fmt(stats.events.completed)}
                                    color="bg-gray-50"
                                />
                            </div>
                        </section>

                        {/* Tickets */}
                        <section>
                            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                Tickets
                            </h2>
                            <div className="grid grid-cols-3 gap-3">
                                <StatCard label="Active Tickets" value={fmt(stats.tickets.total)} />
                                <StatCard
                                    label="New (30d)"
                                    value={fmt(stats.tickets.new_30d)}
                                    color="bg-violet-50"
                                />
                                <StatCard
                                    label="Cancelled"
                                    value={fmt(stats.tickets.cancelled)}
                                    color={stats.tickets.cancelled > 0 ? 'bg-orange-50' : 'bg-white'}
                                />
                            </div>
                        </section>

                        {/* Revenue */}
                        <section>
                            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                Revenue (FestiShare Commission)
                            </h2>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <StatCard
                                    label="All-time Commission"
                                    value={fmtCurrency(stats.revenue.all_time_commission)}
                                    color="bg-green-50"
                                />
                                <StatCard
                                    label="Commission (30d)"
                                    value={fmtCurrency(stats.revenue.commission_30d)}
                                    color="bg-green-50"
                                />
                                <StatCard
                                    label="All-time Gross"
                                    value={fmtCurrency(stats.revenue.all_time_gross)}
                                />
                                <StatCard
                                    label="Gross (30d)"
                                    value={fmtCurrency(stats.revenue.gross_30d)}
                                />
                            </div>
                        </section>

                        {/* Support */}
                        <section>
                            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                Support
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                <StatCard
                                    label="Open Support Threads"
                                    value={fmt(stats.support.open_threads)}
                                    color={stats.support.open_threads > 0 ? 'bg-yellow-50' : 'bg-white'}
                                />
                            </div>
                        </section>
                    </div>
                )}
            </div>
            <Footer />
            <ApplicationBar />
        </div>
    );
};

export default Dashboard;
